import {useEffect, useMemo, useRef, useState} from "react";
import { useParams} from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { createImageForPdf, createPdfFromImages } from "../shared/statelessMethods";
import BllinkUtils from "../utils/utils";
import { PdfRow } from "./PdfRow";
import { useManagementCompanyLogo } from "../hooks/useManagementCompanyLogo";
import { PdfTableReportRenderStyled } from "./PdfTableReportRender.styled";

const DEFAULT_CHUNK_SIZE = 16;
const DEFAULT_CELLS_NUM = 8;
const DEFAULT_PAGE_ORIENTATION = 'p';

const BllinkTime = BllinkUtils.BllinkTime;

export function PdfTableReportRender() {
    const { t } = useTranslation();
    const container = useRef();
    const { partnerID } = useParams();

    const date = BllinkTime.getTimeNow();

    const ltr = useSelector(state => state.common.ltrEnabled);
    const [readyToBuild, setReadyToBuild] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [title, setTitle] = useState(null);
    const [buildingAddress, setBuildingAddress] = useState(null);
    const [partnerName, setPartnerName] = useState(null);
    const [titleRow, setTitleRow] = useState();
    const [cells, setCells] = useState();
    const [currentChunk, setCurrentChunk] = useState(0);
    const [chunkCount, setChunkCount] = useState(null);
    const [currentChunkData, setCurrentChunkData] = useState([]);
    const [images, setImages] = useState([]);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [currentBuildingData, setCurrentBuildingData] = useState(null);
    const [chunkSize, setChunkSize] = useState(DEFAULT_CHUNK_SIZE);
    const [orientation, setOrientation] = useState(DEFAULT_PAGE_ORIENTATION)
    const mcLogo = useManagementCompanyLogo(currentBuildingData?.id, partnerID);

    useEffect(() => {
        if (mcLogo != null && mcLogo.noImage) {
            setIsImageLoaded(true);
        }
    }, [mcLogo]);

    useEffect(() => {
        window.addEventListener('message', handleWindowMessage);
        return () => window.removeEventListener('message', handleWindowMessage);
    }, []);

    function resetState() {
        setCurrentChunk(0);
        setImages([]);
    }

    useEffect(async () => {
        if (readyToBuild && isImageLoaded) {
            if (currentChunk < chunkCount) {
                setCurrentChunkData(reportData.slice(currentChunk*chunkSize, currentChunk*chunkSize+chunkSize));
                const image = await createImageForPdf(container.current, orientation);
                setImages([...images, image]);
                if (!mcLogo?.noImage) {
                    URL.revokeObjectURL(mcLogo);
                }
                setCurrentChunk(current => current + 1);
            } else {
                try {
                    // creating and sending pdf outside for saving later
                    const pdfData = await createPdfFromImages(images, orientation);
                    window.postMessage({
                        bllinkPdfRender: true,
                        pdfDataUrl: pdfData.url,
                        pdfDataBlob: pdfData.blob,
                        iframeError: false
                    })
                } catch (e) {
                    // sending errors outside the iframe
                    window.postMessage({ bllinkPdfRender: true, iframeError: true, error: e });
                }
                setReadyToBuild(false);
                resetState();
            }
        }
    }, [readyToBuild, currentChunk, isImageLoaded]);

    function handleWindowMessage(event) {
        // getting and parsing data from outside
        if (event.data?.applicationData) {
            const {
                data,
                reportTitle,
                buildingAddress,
                titleRow,
                currentBuildingData,
                partnerName,
                chunkSize: chunk,
                orientation: pageOrientation
            } = event.data;
            if (chunk != null) setChunkSize(chunk);
            if (orientation != null) setOrientation(pageOrientation);
            setTitleRow(titleRow);
            setChunkCount(Math.ceil(data.length / chunk));
            setReportData(data);
            setTitle(reportTitle);
            setBuildingAddress(buildingAddress);
            setPartnerName(partnerName);
            setCurrentBuildingData(currentBuildingData);
            // cells - object with names of the columns
            if (titleRow?.[0] != null) setCells(Object.values(titleRow[0]));
            setReadyToBuild(true);
        }
    }

    const name = useMemo(() => {
        return partnerName ? partnerName : currentBuildingData?.managementCompany?.name;
    }, [currentBuildingData, partnerName])

    const renderPdfRows = (row, i) => <PdfRow row={row} key={i} titles={Object.keys(titleRow[0])} />;

    return (
        <PdfTableReportRenderStyled cells={cells?.length || DEFAULT_CELLS_NUM} ltr={ltr} orientation={orientation} >
            <div className='wrapper' ref={container}>
                <div className="pdf-header">
                    <div>{name}</div>
                    <div className="logo">
                        { mcLogo?.noImage === false && (
                            <img src={mcLogo.objectUrl} alt="logo" onLoad={() => setIsImageLoaded(true)} />
                        )}
                    </div>
                </div>

                <div className='pdf-title'>{title}</div>
                <div className='pdf-title address'>{buildingAddress}</div>

                <div className='pdf-rows-wrapper'>
                    <div className='pdf-row'>
                        {cells?.map((header, i) => <div key={i} className='pdf-highlited row-cell'>{header}</div>)}
                    </div>

                    {currentChunkData.map((row, i) => renderPdfRows(row, i))}
                </div>

                <div className="pdf-footer">
                    <div>{t('reports.producedByBllink')}</div>
                    <div>{t('reports.issueDate')} {`${date.day}/${date.month}/${date.year}`}</div>
                </div>
            </div>
        </PdfTableReportRenderStyled>
    );
}
